import { AxiosRequestConfig, AxiosResponse } from "axios";
import { surveyClient } from "@/store";
import { LocalStorageKeys } from "@/survey";

const getInterceptors: any = (maxConcurrentRequests = 5) => {
  let counter = 0;

  const request = (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const sendOrThrottle = (
      config: AxiosRequestConfig,
      readyToExecute: (c: AxiosRequestConfig) => void
    ) => {
      if (counter < maxConcurrentRequests) {
        counter++;
        readyToExecute(config);
      } else {
        setTimeout(() => sendOrThrottle(config, readyToExecute), 200);
      }
    };

    return new Promise(resolve => {
      sendOrThrottle(config, resolve);
    });
  };

  const response = (response: AxiosResponse): AxiosResponse => {
    counter--;
    return response;
  };

  // Guards against several in-flight requests all failing at once: without it,
  // five concurrent 403s produced five alerts and five navigations.
  let sessionExpiryHandled = false;

  const responseError = (error: any): any => {
    counter--;
    console.error("RESPONSE ERROR: " + (typeof error), JSON.stringify(error));
    const key = localStorage.getItem(LocalStorageKeys.TOKEN_LOCAL_STORAGE_KEY);
    // Only the HTTP status is trusted here. Matching "401"/"403" against the
    // message text also fired on unrelated failures whose body or URL happened
    // to contain those digits.
    const status = error.response
      ? error.response.status
      : typeof error.status === "number"
      ? error.status
      : null;
    const authenticationRejected = status === 401 || status === 403;
    const hadSession = typeof key === "string" && key.length > 32;

    if (authenticationRejected && hadSession && !sessionExpiryHandled) {
      sessionExpiryHandled = true;
      surveyClient.logout();
      alert("You have been automatically logged out due to inactivity");

      // Send the browser to the login page rather than reloading the current
      // URL. Reloading dropped the user on the same page with the token
      // already cleared, so the retried request failed the hadSession check
      // above and nothing redirected. On a public route such as /about that
      // left the page rendered with no menu and no way back to the login form.
      const current = window.location.pathname + window.location.search;
      const redirect = current.indexOf("/login") === 0 ? "/" : current;
      window.location.assign("/login?redirect=" + encodeURIComponent(redirect));
    }
    return Promise.reject(error);
  };

  return [request, response, responseError];
};

const [
  requestInterceptor,
  responseInterceptor,
  responseError
] = getInterceptors();

export { requestInterceptor, responseInterceptor, responseError };
